import { Avatar, Box, IconButton, Typography } from '@mui/material';
import { format } from 'date-fns';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { Appointment } from '../../interfaces';
import { getCustomerDetails } from '../../helpers/appointments';
import {
  getCustomerRelationDetailRows,
  getPrimaryCustomerDetails,
} from '../../utils/modal/customerModal';
import { getFullName } from '../../helpers/global';
import { getInitials, isMinor } from '../../utils/global/helper';
import Badge from '../global/Badge';
import NoteBox from './NoteBox';
import {
  INFO_GRID_LABEL_SX,
  INFO_GRID_SX,
  INFO_GRID_VALUE_SX,
} from './sidebarInfoGrid';

interface Props {
  appointment: Appointment;
  onOpenChat?: () => void;
  showChat?: boolean;
  navigateToCustomer: (customerId: string) => void;
}

function GridRow({ label, value }: { label: string; value: string[] }) {
  const lines = value.filter(Boolean);
  if (!lines.length) return null;
  return (
    <>
      <Typography component="span" sx={INFO_GRID_LABEL_SX}>
        {label}
      </Typography>
      <Typography
        component="span"
        sx={{ ...INFO_GRID_VALUE_SX, whiteSpace: 'pre-line' }}>
        {lines.join('\n')}
      </Typography>
    </>
  );
}

function ClientCardBody({
  appointment,
  onOpenChat,
  showChat,
  navigateToCustomer,
}: Props) {
  const customerDetails = getCustomerDetails(appointment);
  const fullName = getFullName(customerDetails);
  const isCustomerMinor = isMinor(
    customerDetails.dob,
    appointment.startDateTime,
  );

  const memberSince =
    !appointment.customerGuest && appointment.customer.createdAt
      ? format(new Date(appointment.customer.createdAt), 'MMM yyyy')
      : null;

  const extraRows = [
    ...getPrimaryCustomerDetails(customerDetails.primaryCustomer),
    ...getCustomerRelationDetailRows(appointment.customer),
  ].filter(row => row.label !== 'Address');

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        width: '100%',
      }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Avatar
          sx={{
            width: 40,
            height: 40,
            fontSize: 'var(--sidebar-font-base)',
            fontWeight: 700,
            bgcolor: 'var(--sidebar-body)',
            color: 'var(--sidebar-surface)',
            flexShrink: 0,
          }}>
          {getInitials(customerDetails)}
        </Avatar>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              sx={{
                fontSize: 'var(--sidebar-font-lg)',
                fontWeight: 700,
                color: 'var(--sidebar-ink)',
              }}
              className="ellipsis">
              {fullName}
            </Typography>
            {isCustomerMinor && (
              <Badge text="Minor" colorScheme="warning" rounded />
            )}
          </Box>
          <Typography
            sx={{
              fontSize: 'var(--sidebar-font-sm)',
              fontWeight: 500,
              color: 'var(--sidebar-secondary)',
            }}>
            {memberSince && `Client since ${memberSince} · `}
            <Box
              component="button"
              type="button"
              onClick={() => navigateToCustomer(appointment.customerId)}
              sx={{
                background: 'none',
                border: 'none',
                padding: 0,
                font: 'inherit',
                fontWeight: 500,
                color: 'var(--sidebar-brand)',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}>
              View profile
            </Box>
          </Typography>
        </Box>
        {showChat && (
          <IconButton
            aria-label="Open chat"
            onClick={onOpenChat}
            sx={{
              width: 36,
              height: 36,
              border: '1px solid var(--sidebar-ink)',
              borderRadius: '5px',
              flexShrink: 0,
            }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 17 }} />
          </IconButton>
        )}
      </Box>

      <Box sx={INFO_GRID_SX}>
        <GridRow label="Phone" value={[customerDetails.phoneNumber || '']} />
        <GridRow label="Email" value={[customerDetails.email || '']} />
        <GridRow
          label="Birthday"
          value={[
            customerDetails.dob
              ? format(
                  new Date(`${customerDetails.dob}T00:00:00`),
                  'MM/dd/yyyy',
                )
              : '',
          ]}
        />
        {extraRows.map(row => (
          <GridRow
            key={row.label}
            label={row.label}
            value={row.value as string[]}
          />
        ))}
      </Box>

      {appointment.customer.internalNotes && (
        <NoteBox
          label="Client note · internal"
          text={appointment.customer.internalNotes}
        />
      )}
    </Box>
  );
}

export default ClientCardBody;
